import React, {useEffect, useRef, useState} from 'react';
import {Button, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField} from "@mui/material";
import moment from "moment";
import {CreateTxnDialog} from "../Common/CreateTxnDialog";
import AreaService from "../../services/AreaService";
import EventBus from "../../EventBus";
import {EventBusEventsEnum} from "../../enums/EventBusEventsEnum";
import {CustomSnackbarTypes} from "../Common/CustomSnackBar";

const areaService = new AreaService();
const BOOK_BY_DISH_VAS = 'BOOK_BY_DISH';

const VasUserMappingListForModule = (props: any) => {
    let [selectedData, setSelectedData] = useState<any>(null);
    let [createTxn, setCreateTxn] = useState<boolean>(false);
    const [dishMultiplierValues, setDishMultiplierValues] = useState<Record<number, string>>({});
    const [savingMultiplier, setSavingMultiplier] = useState<Record<number, boolean>>({});
    const dirtyRows = useRef<Set<number>>(new Set());

    useEffect(() => {
        if (!Array.isArray(props.data)) return;
        const bookByDishRows = props.data.filter(
            (row: any) => row?.vas?.name === BOOK_BY_DISH_VAS && !dirtyRows.current.has(row.id)
        );
        if (bookByDishRows.length === 0) return;

        Promise.all(
            bookByDishRows.map((row: any) => {
                const areaId = row?.userBundleMapping?.house?.areaId;
                if (!areaId) return Promise.resolve({ rowId: row.id, val: null });
                return areaService.fetchOne(areaId)
                    .then((res: any) => ({
                        rowId: row.id,
                        val: res?.data?.bookByDishMultiplier ?? null,
                    }))
                    .catch(() => ({ rowId: row.id, val: null }));
            })
        ).then((results) => {
            const updates: Record<number, string> = {};
            results.forEach(({ rowId, val }: any) => {
                if (val != null) updates[rowId] = String(val);
            });
            if (Object.keys(updates).length > 0) {
                setDishMultiplierValues(prev => ({ ...prev, ...updates }));
            }
        });
    }, [props.data]);

    const handleSaveMultiplier = (rowId: number, areaId: number) => {
        const value = dishMultiplierValues[rowId];
        const parsed = parseFloat(value);
        if (!value || isNaN(parsed)) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Enter a valid multiplier value",
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        setSavingMultiplier(prev => ({ ...prev, [rowId]: true }));
        areaService.updateBookByDishMultiplier(areaId, parsed)
            .then(() => {
                dirtyRows.current.delete(rowId);
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Dish Multiplier Updated Successfully",
                    type: CustomSnackbarTypes.SUCCESS,
                });
            })
            .catch(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Failed to update Dish Multiplier",
                    type: CustomSnackbarTypes.ERROR,
                });
            })
            .finally(() => {
                setSavingMultiplier(prev => ({ ...prev, [rowId]: false }));
            });
    };

    return (
        <div>
            <CreateTxnDialog
                data={selectedData}
                open={createTxn}
                callback={() => {
                    setCreateTxn(false);
                    props.refresh()
                }}
            />
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Id</TableCell>
                            <TableCell>User Bundle</TableCell>
                            <TableCell>Vas Name</TableCell>
                            <TableCell>Total visits</TableCell>
                            <TableCell>Start date</TableCell>
                            <TableCell>End date</TableCell>
                            <TableCell>Total taken</TableCell>
                            <TableCell>CreatedAt</TableCell>
                            <TableCell>UpdatedAt</TableCell>
                            <TableCell>Customer Name</TableCell>
                            <TableCell>Customer Address</TableCell>
                            <TableCell>Customer Mobile</TableCell>
                            <TableCell>Dish Multiplier</TableCell>
                            <TableCell/>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.data.map((row: any) => (
                            <TableRow
                                key={row.id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell scope="row">{row?.id}</TableCell>
                                <TableCell align="center">
                                    <Link href={`/#/UserBundles/${row?.userBundleMappingId}/show`}>
                                        {row?.userBundleMappingId}
                                    </Link>
                                </TableCell>
                                <TableCell align="center">
                                    <Link href={`/#/Vas/${row?.vasId}/show`}>
                                        {row?.vas?.name}
                                    </Link>
                                </TableCell>
                                <TableCell align="center">{row?.totalVisits}</TableCell>
                                <TableCell align="center">
                                    {
                                        !!row?.startDate &&
                                        moment(row?.startDate, 'YYYY-MM-DD').format('DD MMM YYYY')
                                    }
                                </TableCell>
                                <TableCell align="center">
                                    {
                                        !!row?.endDate &&
                                        moment(row?.endDate, 'YYYY-MM-DD').format('DD MMM YYYY')
                                    }
                                </TableCell>
                                <TableCell align="center">{row?.totalTaken}</TableCell>
                                <TableCell align="center">
                                    {
                                        !!row?.createdAt &&
                                        moment(row?.createdAt).format('DD MMM YYYY hh:mm:ss a')
                                    }
                                </TableCell>
                                <TableCell align="center">
                                    {
                                        !!row?.updatedAt &&
                                        moment(row?.updatedAt).format('DD MMM YYYY hh:mm:ss a')
                                    }
                                </TableCell>
                                <TableCell align="center">
                                    <div
                                        style={{
                                            flexDirection: 'column'
                                        }}
                                    >
                                        {
                                            !!row?.userBundleMapping?.house?.customers &&
                                            Array.isArray(row.userBundleMapping.house.customers) &&
                                            row.userBundleMapping.house.customers.map((ele: any) => {
                                                return (
                                                    <p key={ele.fname}>{ele.fname}</p>
                                                )
                                            })
                                        }
                                    </div>
                                </TableCell>
                                <TableCell align="center">{row?.userBundleMapping?.house?.address}</TableCell>
                                <TableCell align="center">
                                    <div
                                        style={{
                                            flexDirection: 'column'
                                        }}
                                    >
                                        {
                                            !!row?.userBundleMapping?.house?.customers &&
                                            Array.isArray(row.userBundleMapping.house.customers) &&
                                            row.userBundleMapping.house.customers.map((ele: any) => {
                                                return (
                                                    <p key={ele.mobile}>{ele.mobile}</p>
                                                )
                                            })
                                        }
                                    </div>
                                </TableCell>
                                <TableCell align="center">
                                    {row?.vas?.name === BOOK_BY_DISH_VAS ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 180 }}>
                                            <TextField
                                                size="small"
                                                label="Multiplier"
                                                value={dishMultiplierValues[row.id] ?? ''}
                                                onChange={(e) => {
                                                    dirtyRows.current.add(row.id);
                                                    setDishMultiplierValues(prev => ({ ...prev, [row.id]: e.target.value }));
                                                }}
                                                style={{ width: 110 }}
                                            />
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                disabled={savingMultiplier[row.id]}
                                                onClick={() => handleSaveMultiplier(row.id, row?.userBundleMapping?.house?.areaId)}
                                            >
                                                {savingMultiplier[row.id] ? 'Saving...' : 'Save'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <span>-</span>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Link href={`/#/UserBundleServiceMappings/${row.id}/show`}>
                                        <Button variant={'contained'} color={'primary'}>
                                            Show
                                        </Button>
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    {
                                        row.totalVisits > row.totalTaken &&
                                        <Button
                                          variant={'contained'}
                                          color={'secondary'}
                                          size={'small'}
                                          onClick={() => {
                                              setSelectedData(row);
                                              setCreateTxn(true);
                                          }}
                                        >
                                          <span style={{width: 'max-content'}}>Create Txn</span>
                                        </Button>
                                    }
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default VasUserMappingListForModule;
